/**
 * Calcul des contrastes de l'accueil — fonctions pures, sans DOM.
 *
 * L'utilisateur choisit librement son fond : on ne peut donc pas figer les
 * couleurs des surfaces « verre » posées dessus. À partir des pixels du fond
 * tel qu'il est affiché (recadré, réduit ≈ flouté par le backdrop-blur), on
 * choisit pour les blocs puis pour le header :
 *  - la teinte (`dark` = voile sombre + texte clair, `light` = voile clair +
 *    texte sombre) qui demande le voile le plus léger ;
 *  - l'opacité minimale de ce voile pour que le texte atteigne le contraste
 *    WCAG AA (4,5:1) sur au moins 95 % de la surface.
 */

export type Rgb = [number, number, number];

export type BackgroundTone = "dark" | "light";

export type BackgroundContrast = {
  /** Teinte des blocs translucides. */
  tone: BackgroundTone;
  /**
   * Opacité du voile des blocs (0–1). En teinte `dark`, 0 signifie « voile
   * blanc de la maquette » (`SHEEN_ALPHA`) : le fond est assez sombre pour le
   * texte clair sans assombrir le verre.
   */
  scrim: number;
  /** Teinte du header (verre sombre + logo blanc, ou verre clair + logo couleur). */
  headerTone: BackgroundTone;
  /** Opacité du voile du header (0–1). */
  headerScrim: number;
};

/** Couleurs de texte de la maquette, aussi utilisées comme voiles. */
export const INK_HEX = "#07070a";
export const PAPER_HEX = "#fcfcfd";
const INK: Rgb = [7, 7, 10];
const PAPER: Rgb = [252, 252, 253];

/** Voile blanc des blocs en verre sur fond sombre (maquette « fond image sombre »). */
export const SHEEN_ALPHA = 0.08;

const TARGET_RATIO = 4.5;
/** Part de la surface qui doit atteindre le ratio (le reste = détails isolés). */
const COVERAGE = 0.95;
const MAX_SCRIM = 0.9;
const SCRIM_STEP = 0.02;

/** Planchers repris de la maquette : en dessous, l'effet verre disparaît. */
const BLOCK_FLOORS = { dark: 0.2, light: 0.35 };
const HEADER_FLOORS = { dark: 0.5, light: 0.6 };

/** Contraste sûr quand le fond n'a pas pu être mesuré. */
export const FALLBACK_CONTRAST: BackgroundContrast = {
  tone: "dark",
  scrim: 0.55,
  headerTone: "dark",
  headerScrim: 0.6,
};

function channelToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Luminance relative WCAG 2.x. */
export function luminance([r, g, b]: Rgb): number {
  return (
    0.2126 * channelToLinear(r) +
    0.7152 * channelToLinear(g) +
    0.0722 * channelToLinear(b)
  );
}

export function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Composition alpha telle que la fait le navigateur (en espace sRGB). */
function composite(bg: Rgb, scrim: Rgb, alpha: number): Rgb {
  return [
    bg[0] * (1 - alpha) + scrim[0] * alpha,
    bg[1] * (1 - alpha) + scrim[1] * alpha,
    bg[2] * (1 - alpha) + scrim[2] * alpha,
  ];
}

/** Le texte reste-t-il lisible sur `COVERAGE` de la surface avec ce voile ? */
function passesAt(pixels: Rgb[], scrim: Rgb, alpha: number, text: Rgb) {
  const textL = luminance(text);
  const allowedFailures = Math.floor(pixels.length * (1 - COVERAGE));
  let failures = 0;
  for (const px of pixels) {
    const ratio = contrastRatio(luminance(composite(px, scrim, alpha)), textL);
    if (ratio < TARGET_RATIO && ++failures > allowedFailures) return false;
  }
  return true;
}

/** Plus petite opacité de voile qui rend le texte lisible, ou `null` si aucune ne suffit. */
function requiredScrim(pixels: Rgb[], scrim: Rgb, text: Rgb): number | null {
  for (let alpha = 0; alpha <= MAX_SCRIM + 1e-9; alpha += SCRIM_STEP) {
    if (passesAt(pixels, scrim, alpha, text)) {
      return Math.round(alpha * 100) / 100;
    }
  }
  return null;
}

/** Teinte qui demande le voile le plus léger pour un texte lisible, et ce voile. */
function pickGlass(
  pixels: Rgb[],
  floors: Record<BackgroundTone, number>,
  allowSheen: boolean,
): { tone: BackgroundTone; scrim: number } {
  const dark = requiredScrim(pixels, INK, PAPER);
  const light = requiredScrim(pixels, PAPER, INK);
  // À besoin égal on garde le verre sombre, plus proche de la maquette sur image.
  const tone: BackgroundTone =
    light !== null && (dark === null || light < dark) ? "light" : "dark";
  // Verre de la maquette sur fond sombre : voile blanc léger, pas noir.
  if (
    tone === "dark" &&
    allowSheen &&
    passesAt(pixels, PAPER, SHEEN_ALPHA, PAPER)
  ) {
    return { tone, scrim: 0 };
  }
  const needed = tone === "dark" ? dark : light;
  return { tone, scrim: Math.max(floors[tone], needed ?? MAX_SCRIM) };
}

/**
 * Teintes et voiles à partir des pixels (déjà réduits/floutés) du fond.
 * `headerPixels` : la bande sous le header (tout le fond par défaut).
 */
export function contrastFromPixels(
  pixels: Rgb[],
  headerPixels: Rgb[] = pixels,
): BackgroundContrast {
  if (pixels.length === 0) return FALLBACK_CONTRAST;
  const blocks = pickGlass(pixels, BLOCK_FLOORS, true);
  const header = pickGlass(
    headerPixels.length > 0 ? headerPixels : pixels,
    HEADER_FLOORS,
    false,
  );
  return {
    tone: blocks.tone,
    scrim: blocks.scrim,
    headerTone: header.tone,
    headerScrim: header.scrim,
  };
}

export function hexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join("") : m[1];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export function contrastForColor(color: string): BackgroundContrast {
  const rgb = hexToRgb(color);
  return rgb ? contrastFromPixels([rgb]) : FALLBACK_CONTRAST;
}

/**
 * Garde de type pour une mesure lue en préférence. `headerTone` est optionnel :
 * les mesures enregistrées avant que le header suive le fond ne l'ont pas.
 */
export function isStoredContrast(
  value: unknown,
): value is Omit<BackgroundContrast, "headerTone"> &
  Partial<Pick<BackgroundContrast, "headerTone">> {
  if (!value || typeof value !== "object") return false;
  const c = value as Record<string, unknown>;
  return (
    (c.tone === "dark" || c.tone === "light") &&
    typeof c.scrim === "number" &&
    typeof c.headerScrim === "number"
  );
}
